import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar, Briefcase, GraduationCap,
  Languages, Award, FileText, MessageSquare, Download, Check, X,
  Clock, User, Building, AlertCircle, Send, Eye,
  CheckCircle, XCircle, Video, MapPinned, Loader, UserCheck, UserX,
} from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api, { LocationDisplay, FILES_URL } from '../../../utils/Api';


const ApplicationDetails = () => {
  const location = useLocation();
  const { id, type } = useParams();
  const navigate = useNavigate();
  console.log(type);
  const { user } = useSelector((state) => state.auth);

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionConfirm, setActionConfirm] = useState({ show: false, action: null });
  const [updating, setUpdating] = useState(false);
  const [showCVModal, setShowCVModal] = useState(false);
  const [imageError, setImageError] = useState(false);



  useEffect(() => {
    setImageError(false);
  }, [id]);


  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    scheduledAt: '',
    location: '',
    meetingLink: '',
    notes: '',
    interviewType: 'in-person'
  });
  const [submittingAppointment, setSubmittingAppointment] = useState(false);
  const [appointmentError, setAppointmentError] = useState(null);

  useEffect(() => {
    fetchApplicationDetails();
  }, [id]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showCVModal) closeCVModal();
        if (showAppointmentModal) resetAppointmentModal();
      }
    };

    if (showCVModal || showAppointmentModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showCVModal, showAppointmentModal]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      let response = null
      if (type === 'jobs') {
        response = await api.get(`/application/${id}`);
      } else {
        response = await api.get(`/mission-application/${id}`);
      }
      setApplication(response.data);
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      // Use appropriate endpoint based on application type
      const endpoint = type === 'missions' ? `/mission-application/${id}` : `/application/${id}`;
      await api.patch(endpoint, { status: newStatus });
      await fetchApplicationDetails();
      setActionConfirm({ show: false, action: null });
    } catch (err) {
      console.error('Error:', err);
      setError('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };


  const [cvBlobUrl, setCvBlobUrl] = useState(null);

  // View CV logic
  const handleViewCV = async () => {
    if (!application.cv) return;

    try {
      setLoading(true); // Re-use loading or add specific loading state if needed
      let folderName = 'cv';
      let fileName = application.cv;

      if (application.cv.includes('/')) {
        const parts = application.cv.split('/');
        fileName = parts.pop();
        if (application.cv.startsWith('http')) {
          const urlParts = application.cv.split('/');
          folderName = urlParts[urlParts.length - 2];
        } else {
          if (parts.length > 0) folderName = parts[parts.length - 1];
        }
      }

      const response = await api.get(`/files/${folderName}/${fileName}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      setCvBlobUrl(url);
      setShowCVModal(true);

    } catch (error) {
      console.error("Error viewing CV:", error);
      alert("Erreur lors de l'ouverture du CV");
    } finally {
      setLoading(false);
    }
  };

  const closeCVModal = () => {
    setShowCVModal(false);
    if (cvBlobUrl) {
      window.URL.revokeObjectURL(cvBlobUrl);
      setCvBlobUrl(null);
    }
  };

  const handleDownloadCV = async (cvUrl) => {
    if (!cvUrl) return;
    try {
      let folderName = 'cv';
      let fileName = cvUrl;

      if (cvUrl.includes('/')) {
        const parts = cvUrl.split('/');
        fileName = parts.pop();
        folderName = parts[parts.length - 1];
      }

      const response = await api.get(`/files/${folderName}/${fileName}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CV:', err);
      alert("Erreur lors du téléchargement du CV");
    }
  };

  const handleAppointmentChange = (field, value) => {
    setAppointmentData(prev => ({ ...prev, [field]: value }));
    if (appointmentError) setAppointmentError(null);
  };

  const validateAppointmentForm = () => {
    if (!appointmentData.scheduledAt) {
      setAppointmentError('La date et l\'heure sont requises');
      return false;
    }

    const selectedDate = new Date(appointmentData.scheduledAt);
    const now = new Date();

    if (selectedDate <= now) {
      setAppointmentError('La date doit être dans le futur');
      return false;
    }

    if (appointmentData.interviewType === 'in-person' && !appointmentData.location.trim()) {
      setAppointmentError('Le lieu est requis');
      return false;
    }

    if (appointmentData.interviewType === 'remote' && !appointmentData.meetingLink.trim()) {
      setAppointmentError('Le lien est requis');
      return false;
    }

    return true;
  };

  const handleScheduleAppointment = async (e) => {
    e.preventDefault();
    setAppointmentError(null);

    if (!validateAppointmentForm()) return;

    try {
      setSubmittingAppointment(true);

      const payload = {
        candidate: application.candidate._id,
        scheduledAt: appointmentData.scheduledAt,
        interviewer: user._id,
        status: 'scheduled',
        context: application._id,
        contextModel: 'Application'
      };

      if (appointmentData.interviewType === 'in-person') {
        payload.location = appointmentData.location;
      } else if (appointmentData.interviewType === 'remote') {
        payload.meetingLink = appointmentData.meetingLink;
      }

      if (appointmentData.notes.trim()) {
        payload.notes = appointmentData.notes;
      }

      await api.post('/appointment', payload);

      resetAppointmentModal();
      await fetchApplicationDetails();
      alert('Entretien planifié avec succès!');

    } catch (err) {
      console.error('Error:', err);
      setAppointmentError(err.response?.data?.message || 'Erreur lors de la planification');
    } finally {
      setSubmittingAppointment(false);
    }
  };

  const resetAppointmentModal = () => {
    setAppointmentData({
      scheduledAt: '',
      location: '',
      meetingLink: '',
      notes: '',
      interviewType: 'in-person'
    });
    setAppointmentError(null);
    setShowAppointmentModal(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const formatDateTime = (dateString, timeString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    })} à ${timeString}`;
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
        icon: Clock,
        text: 'En attente'
      },
      appointmentscheduled: {
        color: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
        icon: Calendar,
        text: 'RDV planifié'
      },
      accepted: {
        color: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        icon: CheckCircle,
        text: 'Acceptée'
      },
      rejected: {
        color: 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700',
        icon: XCircle,
        text: 'Rejetée'
      },
      interview_accepted: {
        color: 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
        icon: Check,
        text: 'Pour Entretien'
      },
      interview_pending: {
        color: 'bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800',
        icon: Calendar,
        text: 'Entretien Prévu'
      },
      interview_process: {
        color: 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
        icon: Video,
        text: 'En Cours d\'Entretien'
      },
      validated: {
        color: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
        icon: CheckCircle,
        text: 'Validée (Recruté)'
      },
      not_validated: {
        color: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
        icon: XCircle,
        text: 'Non Validée'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.text}
      </span>
    );
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const handleSendMessage = async () => {
    const candidateArg = application?.candidate;
    if (!candidateArg?.user_id) {
      alert("Impossible de contacter ce candidat : informations manquantes.");
      return;
    }

    // Handle both populated object and ID string
    const participantId = candidateArg.user_id._id || candidateArg.user_id;

    try {
      const response = await api.post('/chat/conversations', {
        participantId
      });

      // Handle potentially different response structures
      const conversationId = response.data.data?._id || response.data._id;

      if (conversationId) {
        navigate('/dashboard/messages', {
          state: { conversationId }
        });
      } else {
        throw new Error('No conversation ID returned');
      }
    } catch (err) {
      console.error('Error creating conversation:', err);
      alert("Erreur lors de l'initialisation de la conversation.");
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
            <AlertCircle className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Erreur
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dashboard/applications')}
              className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  const candidate = application.candidate;
  const jobOffer = application.jobOffer;
  const mission = application.mission;
  // Determine the title based on whether it's a job or mission application
  const offerTitle = type === 'missions' ? mission?.title?.title : jobOffer?.title?.title;

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard/applications')}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="text-sm font-medium">Retour</span>
            </button>

            <div className="flex items-center gap-2">
              {getStatusBadge(application.status)}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(application.appliedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="flex overflow-hidden h-[calc(100vh-120px)]">
        {/* Left - Candidate Info */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          <div className="p-6">
            {/* Profile */}
            <div className="text-center mb-6">
              {candidate?.profile_picture && !imageError ? (
                <div className="w-20 h-20 mx-auto mb-3 rounded-2xl overflow-hidden">
                  <img
                    src={candidate.profile_picture}
                    alt={`${candidate.first_name} ${candidate.last_name}`}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                </div>
              ) : (
                <div className="w-20 h-20 mx-auto mb-3 rounded-2xl bg-primary-100 dark:bg-primary-950/30 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-500">
                    {candidate?.first_name?.charAt(0)}{candidate?.last_name?.charAt(0)}
                  </span>
                </div>
              )}

              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {candidate.first_name} {candidate.last_name}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {offerTitle}
              </p>
            </div>

            {/* Contact */}
            <div className="space-y-3 mb-6">
              {candidate.user_id?.email && (
                !candidate.user_id.email.includes('*') ? (
                  <a href={`mailto:${candidate.user_id.email}`} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-500">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{candidate.user_id.email}</span>
                  </a>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">Email masqué</span>
                  </div>
                )
              )}
              {candidate.phone_number && (
                !candidate.phone_number.includes('*') ? (
                  <a href={`tel:${candidate.phone_number}`} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-500">
                    <Phone className="h-4 w-4" />
                    {candidate.phone_number}
                  </a>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed">
                    <Phone className="h-4 w-4" />
                    <span>Numéro masqué</span>
                  </div>
                )
              )}
              {candidate.address && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="h-4 w-4" />
                  <span className="truncate">
                    <LocationDisplay
                      cityId={candidate.address.city}
                      wilayaId={candidate.address.wilaya}
                    />
                  </span>
                </div>
              )}
            </div>

            {/* Info cards */}
            {candidate.birth_date && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Âge</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {calculateAge(candidate.birth_date)} ans
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Center - Details */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Experiences */}
            {candidate.experiences && candidate.experiences.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                  Expériences
                </h3>
                <div className="space-y-4">
                  {candidate.experiences.map((exp, index) => (
                    <div key={exp._id || index} className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-950/30 flex items-center justify-center flex-shrink-0">
                        <Building className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">{exp.title}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{exp.company}</p>
                          </div>
                          {exp.is_current && (
                            <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
                              En cours
                            </span>
                          )}
                        </div>
                        {exp.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">{exp.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {candidate.education && candidate.education.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                  Formation
                </h3>
                <div className="space-y-4">
                  {candidate.education.map((edu, index) => (
                    <div key={edu._id || index} className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{edu.degree}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{edu.institution}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">{edu.field}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills & Languages */}
            <div className="grid grid-cols-2 gap-4">
              {candidate.skills && candidate.skills.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Award className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                    Compétences
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {candidate.skills.map((skill, index) => (
                      <span
                        key={skill._id || index}
                        className="px-2 py-1 bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-xs font-medium"
                      >
                        {/* Handle both populated object and potential unpopulated string/ID */}
                        {typeof skill === 'object' ? (skill.name_fr || skill.name_en || skill.name) : skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {candidate.languages && candidate.languages.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Languages className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                    Langues
                  </h3>
                  <div className="space-y-2">
                    {candidate.languages.map((lang, index) => (
                      <div key={lang._id || index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-900 dark:text-white">{lang.language}</span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                          {lang.level}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Questionnaire Answers */}
            {application.answers && application.answers.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                  Questions de pré-sélection
                </h3>
                <div className="space-y-4">
                  {application.answers.map((answer, index) => (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        {answer.questionText}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {typeof answer.answer === 'boolean'
                          ? (answer.answer ? 'Oui' : 'Non')
                          : answer.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cover Letter */}
            {application.coverLetter && application.coverLetterType === 'text' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                  Lettre de motivation
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {application.coverLetter}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right - Actions */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          <div className="p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions rapides</h3>

            {/* Contacter le candidat */}
            <button
              onClick={handleSendMessage}
              className="w-full px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <MessageSquare className="h-4 w-4" />
              Contacter le candidat
            </button>


            {/* Planifier RDV */}
            {(application.status === 'pending' || application.status === 'accepted') && (
              <button
                onClick={() => setShowAppointmentModal(true)}
                className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Calendar className="h-4 w-4" />
                Planifier entretien
              </button>
            )}

            {/* Status Actions - Workflow Buttons */}
            {application.status !== 'validated' && application.status !== 'rejected' && application.status !== 'not_validated' && !actionConfirm.show && (
              <div className="space-y-2">
                {/* JOB WORKFLOW (Simple) */}
                {type === 'jobs' && (
                  <>
                    {application.status === 'pending' && (
                      <>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'accepted' })}
                          className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Check className="h-4 w-4" />
                          Accepter
                        </button>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'rejected' })}
                          className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <X className="h-4 w-4" />
                          Rejeter
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* MISSION WORKFLOW (Admin Mediated) */}
                {type === 'missions' && (
                  <>
                    {/* PENDING -> INTERVIEW ACCEPTED */}
                    {application.status === 'pending' && (
                      <>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'interview_accepted' })}
                          className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Check className="h-4 w-4" />
                          Demander un Entretien (Admin)
                        </button>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'rejected' })}
                          className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <X className="h-4 w-4" />
                          Rejeter
                        </button>
                      </>
                    )}

                    {/* INTERVIEW ACCEPTED -> Waiting for Admin */}
                    {application.status === 'interview_accepted' && (
                      <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl text-center">
                        <Clock className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                          En attente de planification
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          L'administrateur va programmer l'entretien vidéo.
                        </p>
                      </div>
                    )}

                    {/* INTERVIEW PENDING -> INTERVIEW PROCESS */}
                    {application.status === 'interview_pending' && (
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Entretien Planifié</p>
                          <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 mb-2">
                            <Calendar className="h-3 w-3" />
                            {application.interviewDate && formatDateTime(application.interviewDate)}
                          </div>
                          {application.interviewLink && (
                            <a
                              href={application.interviewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 underline"
                            >
                              <Video className="h-3 w-3" /> Lien Visio
                            </a>
                          )}
                        </div>

                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'interview_process' })}
                          className="w-full px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Video className="h-4 w-4" />
                          Démarrer l'Entretien
                        </button>
                      </div>
                    )}

                    {/* INTERVIEW PROCESS -> VALIDATED / NOT VALIDATED */}
                    {application.status === 'interview_process' && (
                      <>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'validated' })}
                          className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <UserCheck className="h-4 w-4" />
                          Valider (Recruter)
                        </button>
                        <button
                          onClick={() => setActionConfirm({ show: true, action: 'not_validated' })}
                          className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <UserX className="h-4 w-4" />
                          Ne pas valider
                        </button>
                      </>
                    )}
                  </>
                )}


              </div>
            )}

            {actionConfirm.show && (
              <div className={`p-4 rounded-lg border-2 ${actionConfirm.action === 'accepted'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
                }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`h-5 w-5 mt-0.5 ${actionConfirm.action === 'accepted'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-600 dark:text-gray-400'
                    }`} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900 dark:text-white mb-2">
                      Confirmer cette action ?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStatusChange(actionConfirm.action)}
                        disabled={updating}
                        className={`px-3 py-1.5 text-xs font-medium text-white rounded-lg ${actionConfirm.action === 'accepted'
                          ? 'bg-emerald-600 hover:bg-emerald-700'
                          : 'bg-gray-600 hover:bg-gray-700'
                          }`}
                      >
                        Confirmer
                      </button>
                      <button
                        onClick={() => setActionConfirm({ show: false, action: null })}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </div>


      {/* CV Modal */}
      {showCVModal && cvBlobUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeCVModal}
        >
          <div
            className="relative w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    CV de {candidate.first_name} {candidate.last_name}
                  </h3>
                  <p className="text-xs text-gray-500">ESC pour fermer</p>
                </div>
              </div>
              <button
                onClick={closeCVModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="w-full h-[calc(100%-72px)]">
              <iframe
                src={`${cvBlobUrl}#view=FitH`}
                className="w-full h-full"
                title="CV"
              />
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal - Same as before */}
      {showAppointmentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={resetAppointmentModal}
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Planifier un Entretien</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {candidate.first_name} {candidate.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={resetAppointmentModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleScheduleAppointment} className="p-6 space-y-5 overflow-y-auto">
              {appointmentError && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">Erreur</p>
                    <p className="text-sm text-red-700 dark:text-red-400 mt-1">{appointmentError}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date et Heure <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  required
                  value={appointmentData.scheduledAt}
                  onChange={(e) => handleAppointmentChange('scheduledAt', e.target.value)}
                  min={getMinDateTime()}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type d'Entretien <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleAppointmentChange('interviewType', 'in-person')}
                    className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'in-person'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <MapPinned className="h-5 w-5" />
                    <span className="text-xs font-medium">En personne</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAppointmentChange('interviewType', 'remote')}
                    className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'remote'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <Video className="h-5 w-5" />
                    <span className="text-xs font-medium">Visio</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAppointmentChange('interviewType', 'phone')}
                    className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'phone'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <Phone className="h-5 w-5" />
                    <span className="text-xs font-medium">Téléphone</span>
                  </button>
                </div>
              </div>

              {appointmentData.interviewType === 'in-person' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lieu <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={appointmentData.location}
                    onChange={(e) => handleAppointmentChange('location', e.target.value)}
                    placeholder="Adresse du lieu"
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>
              )}

              {appointmentData.interviewType === 'remote' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lien de Réunion <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    required
                    value={appointmentData.meetingLink}
                    onChange={(e) => handleAppointmentChange('meetingLink', e.target.value)}
                    placeholder="https://meet.google.com/..."
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (optionnel)
                </label>
                <textarea
                  value={appointmentData.notes}
                  onChange={(e) => handleAppointmentChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Ajouter des notes..."
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={resetAppointmentModal}
                  disabled={submittingAppointment}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submittingAppointment}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingAppointment ? (
                    <span className="inline-flex items-center gap-2">
                      <Clock className="h-4 w-4 animate-spin" />
                      Planification...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      Planifier
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetails;
